import { 
	IDataStore,
	ShareBaseItemService 
} from 'sharebase/domain';
import { AclService } from 'phoenix/services/aclService';
import {
	ShareBaseItem,
	Acl, 
	AclPrincipal,
	AdvancedOption, 
	ObjectType, 
	RightsOption,
	RightsType,
	PermissionType,
	LinkSettings,
	ShareEntity,
	ShareInfo,
	ReceivedShareInfo,
	Permission
} from 'phoenix/domain';

export type ClientCreatedLinkShare = ClientLinkShare & CreatedLinkShare;
interface SharesRetriever {
	() : Acl[]
}

interface LinksRetriever {
	(): ClientLinkShare[];
}

export interface IShareableStore extends IDataStore {
	readonly isSharedContent: boolean;
	type: ObjectType,
	shares: Acl[];
	links:ClientLinkShare[];
	getShares(): ng.IPromise<Acl[]>;
	getLinks(): ng.IPromise<ClientLinkShare[]>;
	createShare(entities:ShareEntity, right:RightsType, advanced:AdvancedOption[]): ng.IPromise<Acl[]>;
	createShares(entities:ShareEntity[], right:RightsType, advanced:AdvancedOption[]): ng.IPromise<Acl[]>;
	updateShare(updatedShare:Acl, right:RightsType, advanced:AdvancedOption[]): ng.IPromise<Acl>;
	deleteShare(deletedShare:Acl): ng.IPromise<Acl[]>;	
	createLink(share:ServerLinkShare, right: RightsOption): ng.IPromise<ClientCreatedLinkShare>;
	revokeLink(link:LinkShare): ng.IPromise<ClientLinkShare>;
	messageSharedUsers(subject:string, message:string): ng.IPromise<any>;
	getPermissions(share:Acl, isInherited?:boolean): ng.IPromise<RightsRestrictions>;
	getLinkRights(userAcl:Acl): ng.IPromise<RightsRestrictions>;
	getCurrentUserAcl(): ng.IPromise<Acl>;
}

export interface IInheritedShareableStore extends IShareableStore{
	inheritedShares: Acl[];
	inheritedLinks: ClientLinkShare[];
}

export interface IShareableService extends ShareBaseItemService {
	getShares(item:ShareBaseItem, isCompact?:Boolean): ng.IPromise<ReceivedShareInfo[]>;
	createShare(item:ShareBaseItem, share:ShareInfo):ng.IHttpPromise<any>;
	updateShare(item:ShareBaseItem, share:ShareInfo):ng.IHttpPromise<any>;
	deleteShare(item:ShareBaseItem, share:ShareInfo):ng.IHttpPromise<any>;
	getPermissionsForCurrentUser(item:ShareBaseItem): ng.IPromise<ReceivedShareInfo[]>;
	getLinks(item:ShareBaseItem): ng.IPromise<ReceivedLinkShare[]>;
	createLink(item:ShareBaseItem, share:ServerLinkShare): ng.IPromise<CreatedLinkShare>;	
	revokeLink(item:LinkShare): ng.IPromise<CreatedLinkShare>;	
	getRights(currentUserBits:number, targetUserBits?:number, isInherited?:boolean): ng.IPromise<RightsRestrictions>;
	getLinkRights(rightsBits:number): ng.IPromise<RightsRestrictions>;
}

export interface RightsRestrictions {
	maxLinkExpirationDays: number;
	maxLinkExpirationType: number; // TODO: convert to enum
	maxLinkAuthenticationType: number; // TODO: convert to enum
	minLinkAuthenticationType?: UserGroupLinkRestriction;
	rights: RightsOption[]
}

export enum LinkSecurity {
	Default = -1,
	None = 0,
	Password = 2,	
	AccessCode = 8
}

export enum UserGroupLinkRestriction {
	None = 0,
	RequirePin = 100,
	Default = -1
}

export interface LinkShare {
	AuthenticationRequired?: boolean;
	DateExpires: string | Date;
	DateShared: string | Date;
	IsExpired: boolean;
	Links: {
		Content?: string;
		ActivityUri: string;
		RevokeUri: string;
		Self: string;
		Update: string;
		Validate: string;
	};	
	ReferenceString: string;
	Sender: string;
}

export interface ReceivedLinkShare extends LinkShare{
	Id: number;
	ObjectId: number;
	Settings: LinkSecurity;
	ObjectName: string;
	ObjectType: string;
	Permissions: number;
	Embedded: {
		Recipients: string[];
	}
}

export interface CreatedLinkShare extends LinkShare {
	PinRequired: boolean;
	PasswordRequired: boolean;
	AuthToken: string;
	Embedded: {
		Permissions: Permission[];
		Recipients: string[];
	};
}

export interface LinkCreationError {
	Error: string;
	InvalidPassword: boolean;
}

export interface FolderLinkShare extends CreatedLinkShare {
	FolderName: string;
}

export interface DocumentLinkShare extends CreatedLinkShare {
	DocumentName:string;
}

export interface ServerLinkShare {
	CustomEmailText: string;
	Embedded: {
		Permissions: Permission[]
	};
	ExpirationValue: number;
	ExpireMeaning: string;
	ExpiresOn: Date;
	Password: string;
	PinRequired: boolean;
	Recipients: string[];
}

export interface ClientLinkShare extends LinkShare {
	ClientId: string;
	RightString: string;
	ExpireString: string;
	IsRevoked: boolean;
	IsInherited: boolean;
	DeleteExpanded: boolean;
	Settings: LinkSecurity;
	LinkUrl:string;
}

export interface IShareServiceFactory {
	create(service:IShareableService, store: IShareableStore): IShareService;
}

export interface IShareService {
	getShares(item:ShareBaseItem): ng.IPromise<Acl[]>;
	getLinks(item:ShareBaseItem): ng.IPromise<ClientLinkShare[]>;
	createShare(item:ShareBaseItem, entity:ShareEntity, right:RightsType, advanced:AdvancedOption[]): ng.IPromise<Acl[]>
	createShares(item:ShareBaseItem, entities:ShareEntity[], right:RightsType, advanced:AdvancedOption[]): ng.IPromise<Acl[]>
	createLink(item:ShareBaseItem, share:ServerLinkShare, right:RightsOption): ng.IPromise<ClientCreatedLinkShare>;
	revokeLink(item:ClientLinkShare): ng.IPromise<ClientLinkShare[]>;
	updateShare(item:ShareBaseItem, updatedShare:Acl, right:RightsType, advanced:AdvancedOption[]): ng.IPromise<Acl>;
	deleteShare(item:ShareBaseItem, deletedShare:Acl): ng.IPromise<Acl[]>;
	getCurrentUserAcl(item:number|ShareBaseItem): ng.IPromise<Acl>;
	getRights(sharer:Acl, share?:Acl, isInherited?:boolean): ng.IPromise<RightsRestrictions>;
	getLinkRights(sharer:Acl): ng.IPromise<RightsRestrictions>;
	sendShareMessage(item:ShareBaseItem, subject:string, message:string): ng.IHttpPromise<any>;
}



// WEBPACK FOOTER //
// ./src/sharebase/domain/sharebase-sharing.ts